import { ChevronDown, ChevronUp } from "lucide-react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import type { InboundDoc, InboundFilterState, InboundSortKey } from "../lib/inbound-filter";

import { DirectionChip } from "./DirectionChip";

interface InboundTableProps {
  rows: InboundDoc[];
  filter: InboundFilterState;
  onSort: (key: InboundSortKey) => void;
}

const SORTABLE_COLUMNS: InboundSortKey[] = [
  "doc_no",
  "doc_type",
  "load_date",
  "load_time",
  "sku",
  "quantity",
  "tran_type",
  "ref_no",
];

export function InboundTable({ rows, filter, onSort }: InboundTableProps): JSX.Element {
  const { t, i18n } = useTranslation("inbound_report");

  const fmt = new Intl.NumberFormat(i18n.language);

  function SortIcon({ colKey }: { colKey: InboundSortKey }): JSX.Element | null {
    if (filter.sortKey !== colKey) return null;
    return filter.sortDir === "asc" ? (
      <ChevronUp className="ml-1 inline h-3.5 w-3.5" aria-hidden />
    ) : (
      <ChevronDown className="ml-1 inline h-3.5 w-3.5" aria-hidden />
    );
  }

  return (
    <div className="overflow-x-auto rounded-[14px] border border-[var(--border-subtle)] shadow-[var(--sh-1)]">
      <table className="min-w-full border-collapse bg-[var(--surface-raised)]">
        <thead className="border-b border-[var(--border-subtle)] bg-[var(--surface-raised)]">
          <tr>
            {SORTABLE_COLUMNS.map((key) => (
              <th
                key={key}
                data-testid={`inbound-th-${key}`}
                className="h-11 cursor-pointer select-none whitespace-nowrap px-[var(--sp-4)] text-left text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)] transition hover:text-[var(--text-primary)]"
                onClick={() => onSort(key)}
              >
                {t(`column.${key}`)}
                <SortIcon colKey={key} />
              </th>
            ))}
            {/* remark column is not sortable */}
            <th className="h-11 select-none whitespace-nowrap px-[var(--sp-4)] text-left text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)]">
              {t("column.remark")}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--border-subtle)]">
          {rows.map((r) => (
            <tr
              key={r.id}
              data-testid={`inbound-row-${r.id}`}
              data-sku={r.sku}
              className="h-[var(--row-height-default)] transition hover:bg-[var(--surface-spotlight)]"
            >
              <td className="sku whitespace-nowrap px-[var(--sp-4)] text-sm text-[var(--text-primary)]">
                {r.doc_no}
              </td>
              <td className="whitespace-nowrap px-[var(--sp-4)] text-sm text-[var(--text-secondary)]">
                {r.doc_type}
              </td>
              <td className="whitespace-nowrap px-[var(--sp-4)] text-sm text-[var(--text-secondary)]">
                {r.load_date}
              </td>
              <td className="whitespace-nowrap px-[var(--sp-4)] text-sm text-[var(--text-secondary)]">
                {r.load_time}
              </td>
              <td className="sku whitespace-nowrap px-[var(--sp-4)] text-sm text-[var(--text-primary)]">
                {r.sku}
              </td>
              <td className="num whitespace-nowrap px-[var(--sp-4)] text-right text-sm font-semibold text-[var(--text-primary)]">
                {fmt.format(r.quantity)}
              </td>
              <td className="whitespace-nowrap px-[var(--sp-4)]">
                <DirectionChip direction={r.tran_type} testId={`inbound-direction-${r.id}`} />
              </td>
              <td className="px-[var(--sp-4)] text-sm text-[var(--text-secondary)]">
                {r.ref_no || t("state.none")}
              </td>
              <td className="px-[var(--sp-4)] text-sm text-[var(--text-secondary)]">{r.remark}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
